import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useQuery} from '@apollo/client'
import {GET_ALL_CAMPAIGNS, GET_LABELS} from '../utils/api/campaign'
import {
  GET_ALL_ADVERTISERS_FOR_FILTER,
  createAdvertiserFilters,
} from '../utils/advertiser-helpers'
import {Button, Dropdown, Input, Modal, Tooltip} from 'antd'
import type {MenuProps} from 'antd'
import {DownOutlined, DeleteOutlined} from '@ant-design/icons'
import Table from '../components/lists/Table/Table'
import Breadcrumb from '../components/navigation/Breadcrumb'
import {useCurrentUser} from '../utils/CurrentUserContext'
import {
  Campaign,
  Label,
} from '../__generated__/graphql'
import {useCampaignsWithCost} from '../utils/api/cube'
import {useQueryVariables} from '../utils/hooks'
import {useAnalytics} from '../utils/hooks/useAnalytics'
import {PerformanceChart} from '../components/campaigns/PerformanceChart'
import { getCampaignsColumns } from '../utils/campaign-utils'
import { trackEvent } from '../utils/analytics'
import { safeInt, sameArray } from '../utils/helpers'
import type { Key } from 'react'

const pages = [{name: 'Campaigns', href: '/campaigns', current: true}]
const PAGE_SIZE_OPTIONS = ['10', '25', '50', '100']
const SAVED_VIEWS_STORAGE_KEY = 'campaignsSavedViews'

type SavedViewFilters = {
  labels?: string[]
  status?: string[]
  advertiserId?: string[]
  search?: string
}

type SavedView = {
  name: string
  filters: SavedViewFilters
  sort: {
    column: string
    direction: string
  }
  pagination: {
    page?: number
    pageSize?: number
  }
}

// Keeps URLs clean by removing parameters that match their default values
// Example: setOrDelete(url, 'page', 1, 1) removes 'page' from URL, but setOrDelete(url, 'page', 5, 1) sets 'page=5'
export const setOrDelete = (
  urlParams: URLSearchParams,
  key: string,
  val: string | number | undefined | null,
  defaultVal?: string | number
) => {
  if (val == null || (defaultVal != null && String(val) === String(defaultVal))) {
    urlParams.delete(key)
  } else {
    urlParams.set(key, String(val))
  }
}

const CampaignsTest = () => {
  const {user} = useCurrentUser()
  useAnalytics()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<Key[]>([])
  const [selectedCampaignNames, setSelectedCampaignNames] = useState<Record<string, string>>({})
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => {
    if (typeof window === 'undefined') return []
    const stored = window.localStorage.getItem(SAVED_VIEWS_STORAGE_KEY)
    if (!stored) return []
    try {
      return JSON.parse(stored) as SavedView[]
    } catch (e) {
      console.warn('Failed to parse saved campaign views', e)
      return []
    }
  })
  const [isSaveModalVisible, setIsSaveModalVisible] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [isSavedViewsOpen, setIsSavedViewsOpen] = useState(false)
  const [viewPendingDelete, setViewPendingDelete] = useState<string | null>(null)
  const defaultFilters = {
    status: searchParams.getAll('status'),
    onlyOwned: true,
    labels: searchParams.getAll('labels'),
    advertiserId: searchParams.getAll('advertiserId'),
    search: searchParams.get('search') || '',
  }

  // Extract sort information from URL parameters
  const defaultSort = {
    column: searchParams.get('sortColumn') || 'createdAt',
    direction: searchParams.get('sortDirection') || 'DESC'
  }

  // Read pagination from URL with safe parsing
  const defaultPagination = {
    current: safeInt(searchParams.get('page'), 1),
    pageSize: safeInt(searchParams.get('pageSize'), 10),
  }

  const {variables, setPagination, setFilters, setSort} =
    useQueryVariables(defaultFilters, defaultSort, defaultPagination)

  const {loading, error, data} = useQuery(GET_ALL_CAMPAIGNS, {
    variables,
  })

  console.log('Query variables:', JSON.stringify(variables))

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(SAVED_VIEWS_STORAGE_KEY, JSON.stringify(savedViews))
  }, [savedViews])

  // Reset pagination if on empty page or beyond total pages
  useEffect(() => {
    const items = data?.campaigns?.items || []
    const totalPages = data?.campaigns?.meta?.totalPages || 1
    const currentPage = variables.page || 1

    // Reset if on empty page OR beyond total pages (clamp to totalPages for better UX)
    if (!loading && currentPage > 1 && (items.length === 0 || currentPage > totalPages)) {
      setPagination({
        current: Math.max(1, totalPages),  // Clamp to totalPages instead of always jumping to 1
        pageSize: variables.perPage || 10,
      })
    }
  }, [loading, data, variables.page, variables.perPage, setPagination])

  // Development warning for pagination mismatch
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && data?.campaigns?.meta) {
      const frontendPages = Math.ceil(data.campaigns.meta.totalItems / (variables.perPage || 10))
      const backendPages = data.campaigns.meta.totalPages

      if (frontendPages !== backendPages) {
        console.warn(
          `⚠️ Pagination mismatch: Frontend calculated ${frontendPages} pages, ` +
          `backend says ${backendPages} pages. ` +
          `TotalItems: ${data.campaigns.meta.totalItems}, PageSize: ${variables.perPage}`
        )
      }
    }
  }, [data, variables.perPage])

  const campaigns = useMemo(() => data?.campaigns?.items || [], [data])
  const campaignsWithCost = useCampaignsWithCost(campaigns)
  const {data: labelsData, loading: labelsLoading} = useQuery(GET_LABELS, {
    variables: {perPage: 1000, page: 1},
  })
  const labels = labelsLoading ? [] : labelsData.labels.items
  const labelLookup = useMemo(
    () => Object.fromEntries(labels.map((l: Label) => [l._id, l.name])),
    [labels]
  )

  // Test the advertiser filter function by querying all advertisers
  const {data: advertisersData} = useQuery(GET_ALL_ADVERTISERS_FOR_FILTER, {
    variables: {
      perPage: 1000,
    },
  })

  const advertiserFilters = useMemo(() => {
    if (advertisersData) {
      return createAdvertiserFilters(advertisersData)
    }

    if (loading || !campaigns) return []
    const uniqueAdvertisers = new Set<string>()
    campaigns.forEach((campaign: Campaign) => {
      if (campaign.advertiser && campaign.advertiser.name) {
        uniqueAdvertisers.add(campaign.advertiser.name)
      }
    })
    return Array.from(uniqueAdvertisers).map((advertiser) => ({
      text: advertiser,
      value: advertiser,
    }))
  }, [campaigns, loading, advertisersData])

  const advertiserLookup = useMemo(() => {
    const entries = advertiserFilters.map((a: any) => [String(a.value), a.text])
    return Object.fromEntries(entries)
  }, [advertiserFilters])

  const navigate = useNavigate()

  const columns = getCampaignsColumns(
    user.company,
    undefined,
    labels,
    advertiserFilters,
    defaultFilters,
    navigate
  )

  const handleRowClicked = (record: any, event?: any) => {
    // Don't navigate if clicking on a link or interactive element
    if (event?.target?.tagName === 'A' || event?.target?.closest('a')) {
      return
    }
    
    // Don't navigate if clicking in the checkbox column area
    // Check if the click is within the checkbox cell (first column)
    const clickedElement = event?.target
    const cell = clickedElement?.closest('td')
    if (cell) {
      const row = cell.closest('tr')
      const cellIndex = Array.from(row?.cells || []).indexOf(cell)
      // If clicking on the first column (checkbox column), toggle selection instead
      if (cellIndex === 0) {
        const currentlySelected = selectedCampaignIds.includes(record._id)
        if (currentlySelected) {
          // Remove from selection
          const newIds = selectedCampaignIds.filter(id => id !== record._id)
          const newNames = { ...selectedCampaignNames }
          delete newNames[record._id]
          handleRowSelectionChange(newIds, campaigns.filter(c => newIds.includes(c._id)))
        } else {
          // Add to selection
          const newIds = [...selectedCampaignIds, record._id]
          const newNames = { ...selectedCampaignNames, [record._id]: record.name }
          handleRowSelectionChange(newIds, [...campaigns.filter(c => selectedCampaignIds.includes(c._id)), record])
        }
        return
      }
    }
    
    // Check if user is the campaign owner
    const isOwner = record.ownerCompanyId === user.company._id

    // If user is the owner, go to details page, otherwise go to reporting
    if (isOwner) {
      navigate(`/campaigns/${record._id}/details`)
    } else {
      navigate(`/campaigns/${record._id}/report`)
    }
  }

  // Handle row selection change
  const handleRowSelectionChange = (selectedRowKeys: Key[], selectedRows: any[]) => {
    setSelectedCampaignIds(selectedRowKeys)
    
    // Build name mapping
    const nameMap: Record<string, string> = {}
    selectedRows.forEach(row => {
      nameMap[row._id] = row.name
    })
    setSelectedCampaignNames(nameMap)
  }

  const applySavedView = useCallback((view?: SavedView) => {
    if (!view) return

    const viewFilters = view.filters || {}
    const nextFilters = {
      labels: Array.isArray(viewFilters.labels) ? viewFilters.labels : [],
      status: Array.isArray(viewFilters.status) ? viewFilters.status : [],
      advertiserId: Array.isArray(viewFilters.advertiserId) ? viewFilters.advertiserId : [],
      search: viewFilters.search || '',
      onlyOwned: true,
    }

    const nextSort = view.sort || variables.sort || defaultSort
    const nextPage = view.pagination?.page || 1
    const nextPageSize = view.pagination?.pageSize || 10

    setFilters(nextFilters)
    setSort(nextSort)
    setPagination({
      current: nextPage,
      pageSize: nextPageSize,
    })

    const urlParams = new URLSearchParams()
    nextFilters.labels.forEach(l => urlParams.append('labels', String(l)))
    nextFilters.status.forEach(s => urlParams.append('status', String(s)))
    nextFilters.advertiserId.forEach(a => urlParams.append('advertiserId', String(a)))
    setOrDelete(urlParams, 'search', nextFilters.search, '')
    setOrDelete(urlParams, 'page', nextPage, 1)
    setOrDelete(urlParams, 'pageSize', nextPageSize, 10)

    if (nextSort?.column) urlParams.set('sortColumn', String(nextSort.column))
    if (nextSort?.direction) urlParams.set('sortDirection', String(nextSort.direction))

    setSearchParams(urlParams, {replace: true})
  }, [defaultSort, setFilters, setPagination, setSearchParams, setSort, variables.sort])

  const handleSaveView = useCallback(() => {
    const trimmedName = newViewName.trim()
    if (!trimmedName) return

    const currentFilters = (variables.filters || {}) as SavedViewFilters
    const view: SavedView = {
      name: trimmedName,
      filters: {
        labels: currentFilters.labels || [],
        status: currentFilters.status || [],
        advertiserId: currentFilters.advertiserId || [],
        search: currentFilters.search || '',
      },
      sort: variables.sort,
      pagination: {
        page: variables.page || 1,
        pageSize: variables.perPage || 10,
      },
    }

    setSavedViews(prev => {
      const existingIndex = prev.findIndex(v => v.name === trimmedName)
      if (existingIndex !== -1) {
        const updated = [...prev]
        updated[existingIndex] = view
        return updated
      }
      return [...prev, view]
    })

    setIsSaveModalVisible(false)
    setNewViewName('')
  }, [newViewName, setSavedViews, variables.filters, variables.page, variables.perPage, variables.sort])

  const savedViewsMenuItems = useMemo<MenuProps['items']>(() => (
    savedViews.map((view) => {
      const filters = view.filters || {}
      const labelNames = (filters.labels || []).map(id => labelLookup[id] || id)
      const advertiserNames = (filters.advertiserId || []).map(id => advertiserLookup[String(id)] || id)
      const filterParts = [
        filters.search ? `Search: "${filters.search}"` : null,
        labelNames.length ? `Labels: ${labelNames.join(', ')}` : null,
        filters.status?.length ? `Status: ${filters.status.join(', ')}` : null,
        advertiserNames.length ? `Advertisers: ${advertiserNames.join(', ')}` : null,
      ].filter(Boolean)

      const sortPart = view.sort ? `Sort: ${view.sort.column} ${view.sort.direction}` : null
      const pageSizePart = view.pagination?.pageSize ? `Page size: ${view.pagination.pageSize}` : null
      const tooltip = [filterParts.join(' | ') || 'No filters', sortPart, pageSizePart]
        .filter(Boolean)
        .join(' • ')

      return {
        key: view.name,
        label: (
          <div className="flex items-center justify-between gap-2">
            <Tooltip title={tooltip} placement="left">
              <span>{view.name}</span>
            </Tooltip>
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setViewPendingDelete(view.name)
                setIsSavedViewsOpen(false)
              }}
            />
          </div>
        ),
      }
    })
  ), [savedViews, labelLookup, advertiserLookup])

  const handleSavedViewSelect: MenuProps['onClick'] = ({key}) => {
    const view = savedViews.find(v => v.name === key)
    setIsSavedViewsOpen(false)
    applySavedView(view)
  }

  const handleDeleteView = () => {
    if (!viewPendingDelete) return
    setSavedViews(prev => prev.filter(v => v.name !== viewPendingDelete))
    setViewPendingDelete(null)
  }

  // Handle table changes (pagination, filters, sorting)
  const handleTableChange = useCallback((pagination, filters, sorter) => {
    const nextPageSize = pagination?.pageSize || variables.perPage || 10
    const isPageSizeChange = nextPageSize !== variables.perPage

    const updatedFilters = {
      labels: filters.labels || [],
      status: filters.status || [],
      onlyOwned: true,
      search: filters.search || '',
      advertiserId: filters.advertiserId || [],
    }

    // Check if filters have changed (efficient array comparison)
    const hasFiltersChanged =
      !sameArray(updatedFilters.labels, (variables.filters as any).labels || []) ||
      !sameArray(updatedFilters.status, (variables.filters as any).status || []) ||
      !sameArray(updatedFilters.advertiserId, (variables.filters as any).advertiserId || []) ||
      updatedFilters.search !== ((variables.filters as any).search || '')

    // Check if sort changed
    const hasSortChanged = sorter?.field && (
      sorter.field !== variables.sort.column ||
      (sorter.order === 'ascend' ? 'ASC' : 'DESC') !== variables.sort.direction
    )

    // Reset to page 1 if page size, filters, or sort changed
    const nextPage = (isPageSizeChange || hasFiltersChanged || hasSortChanged)
      ? 1
      : pagination?.current || 1

    setPagination({
      current: nextPage,
      pageSize: nextPageSize,
    })

    // Clone URL params to preserve unrelated params
    const urlParams = new URLSearchParams(searchParams)

    // Pagination (clean URLs - delete defaults)
    setOrDelete(urlParams, 'page', nextPage, 1)
    setOrDelete(urlParams, 'pageSize', nextPageSize, 10)

    // Filters (clear before appending)
    urlParams.delete('labels')
    urlParams.delete('status')
    urlParams.delete('advertiserId')

    updatedFilters.labels?.forEach(l => urlParams.append('labels', String(l)))
    updatedFilters.status?.forEach(s => urlParams.append('status', String(s)))
    updatedFilters.advertiserId?.forEach(a => urlParams.append('advertiserId', String(a)))
    setOrDelete(urlParams, 'search', updatedFilters.search, '')

    // Handle sorting (preserve existing sort when sorter is absent)
    if (sorter && sorter.field) {
      const sortDirection = sorter.order === 'ascend' ? 'ASC' : 'DESC'
      const sortColumn = sorter.field

      urlParams.set('sortColumn', String(sortColumn))
      urlParams.set('sortDirection', String(sortDirection))

      setSort({
        column: sortColumn,
        direction: sortDirection
      })
    } else {
      // Keep existing sort (don't clobber URL-driven sort)
      urlParams.set('sortColumn', String(variables.sort.column))
      urlParams.set('sortDirection', String(variables.sort.direction))
    }

    // Smart history management
    const historyMode = (isPageSizeChange || nextPage !== variables.page)
      ? { replace: true }  // Pagination = replace (don't pollute history)
      : {}                  // Filters/Sort = push (allow back button)

    setSearchParams(urlParams, historyMode)
    setFilters(updatedFilters)

    // Analytics with trigger type
    trackEvent('campaign_list_pagination_changed', {
      trigger: isPageSizeChange ? 'pageSize'
        : hasFiltersChanged ? 'filters'
        : hasSortChanged ? 'sort'
        : 'page',
      page: nextPage,
      pageSize: nextPageSize,
      totalItems: data?.campaigns?.meta?.totalItems,
    })
  }, [searchParams, variables, setSearchParams, setPagination, setFilters, setSort, data])

  const pageSizeOptions = useMemo(() => {
    const options = [...PAGE_SIZE_OPTIONS]
    const currentSize = String(variables.perPage || 10)
    if (!options.includes(currentSize)) {
      options.push(currentSize)
    }
    return options
  }, [variables.perPage])

  return (
    <div>
      <style>{`
        .campaigns-table .ant-table-selection-column {
          cursor: pointer;
        }
        .campaigns-table tbody tr td:first-child {
          cursor: pointer;
          background-color: rgba(0, 0, 0, 0.01);
        }
        .campaigns-table tbody tr:hover td:first-child {
          background-color: rgba(0, 0, 0, 0.02);
        }
      `}</style>
      <div className='mb-4 flex items-center justify-between'>
        <div className="flex items-center gap-4">
          <Breadcrumb pages={pages} />
        </div>
        <div className='flex items-center gap-3'>
          {savedViews.length > 0 && (
            <Dropdown.Button
              className="saved-views-dropdown"
              menu={{items: savedViewsMenuItems, onClick: handleSavedViewSelect}}
              placement="bottomRight"
              trigger={['click']}
              type="default"
              icon={<DownOutlined />}
              open={isSavedViewsOpen}
              onOpenChange={setIsSavedViewsOpen}
              onClick={() => setIsSavedViewsOpen(true)}
            >
              Saved Views
            </Dropdown.Button>
          )}
          <Button onClick={() => setIsSaveModalVisible(true)}>
            Save View
          </Button>
        </div>
      </div>
      
      {/* Performance Chart */}
      <PerformanceChart 
        selectedCampaignIds={selectedCampaignIds as string[]}
        selectedCampaignNames={selectedCampaignNames}
      />
      
      <Table
        columns={columns}
        data={campaignsWithCost}
        rowKey="_id"
        onRowClicked={handleRowClicked}
        loading={loading}
        selectable={true}
        defaultRowSelection={selectedCampaignIds}
        onRowSelectionChange={handleRowSelectionChange}
        className="campaigns-table"
        pagination={{
          total: data ? data.campaigns.meta.totalItems : 0,
          pageSize: variables.perPage,
          current: variables.page,
          showSizeChanger: true,
          pageSizeOptions,
        }}
        defaultFilters={defaultFilters}
        onChange={handleTableChange}
      />
      <Modal
        title="Save current view"
        open={isSaveModalVisible}
        okText="Save"
        okButtonProps={{disabled: newViewName.trim().length === 0}}
        onOk={handleSaveView}
        onCancel={() => setIsSaveModalVisible(false)}
        destroyOnClose
      >
        <Input
          autoFocus
          placeholder="Name this view"
          value={newViewName}
          onChange={(e) => setNewViewName(e.target.value)}
          onPressEnter={handleSaveView}
        />
      </Modal>
      <Modal
        title="Delete view"
        open={!!viewPendingDelete}
        okText="Delete"
        okButtonProps={{danger: true}}
        onOk={handleDeleteView}
        onCancel={() => setViewPendingDelete(null)}
        destroyOnClose
      >
        <p>Are you sure you want to delete this view?</p>
      </Modal>
    </div>
  )
}

export default CampaignsTest
